import { supabase } from './supabase';
import { Post, User } from '../types';

// Search functionality
export const searchUsers = async (query: string, limit: number = 20): Promise<User[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return [];
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .or(`username.ilike.%${query}%,full_name.ilike.%${query}%`)
      .limit(limit);

    if (error) {
      console.error('Supabase error in searchUsers:', error);
      return [];
    }

    return (data || []).map(profile => ({
      id: profile.id,
      username: profile.username,
      displayName: profile.full_name || profile.username,
      profileImage: profile.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
      bio: profile.bio || '',
      followerCount: 0,
      followingCount: 0,
      postCount: 0,
    }));
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
};

export const searchHashtags = async (query: string, limit: number = 20): Promise<{ hashtag: string; count: number }[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return [];
    }

    const { data, error } = await supabase
      .from('post_hashtags')
      .select('hashtag')
      .ilike('hashtag', `%${query}%`)
      .limit(limit);

    if (error) {
      console.error('Supabase error in searchHashtags:', error);
      return [];
    }

    // Count occurrences of each hashtag
    const hashtagCounts: { [key: string]: number } = {};
    (data || []).forEach(item => {
      hashtagCounts[item.hashtag] = (hashtagCounts[item.hashtag] || 0) + 1;
    });

    return Object.entries(hashtagCounts)
      .map(([hashtag, count]) => ({ hashtag, count }))
      .sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error('Error searching hashtags:', error);
    return [];
  }
};

export const searchPosts = async (query: string, limit: number = 20): Promise<Post[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return [];
    }

    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        profiles:user_id (
          id,
          username,
          full_name,
          avatar_url
        ),
        post_media (
          id,
          media_url,
          media_type,
          order_index,
          width,
          height
        ),
        post_hashtags (
          id,
          hashtag
        ),
        post_likes (
          id,
          user_id
        ),
        post_comments (
          id
        )
      `)
      .or(`caption.ilike.%${query}%,location.ilike.%${query}%`)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Supabase error in searchPosts:', error);
      return [];
    }

    return transformPostsData(data || []);
  } catch (error) {
    console.error('Error searching posts:', error);
    return [];
  }
};

export const searchByHashtag = async (hashtag: string, limit: number = 20): Promise<Post[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return [];
    }

    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        profiles:user_id (
          id,
          username,
          full_name,
          avatar_url
        ),
        post_media (
          id,
          media_url,
          media_type,
          order_index,
          width,
          height
        ),
        post_hashtags!inner (
          hashtag
        ),
        post_likes (
          id,
          user_id
        ),
        post_comments (
          id
        )
      `)
      .eq('post_hashtags.hashtag', hashtag.toLowerCase())
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Supabase error in searchByHashtag:', error);
      return [];
    }

    return transformPostsData(data || []);
  } catch (error) {
    console.error('Error searching by hashtag:', error);
    return [];
  }
};

// Explore/Discovery functionality
export const getPopularPosts = async (limit: number = 20): Promise<Post[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return [];
    }

    // Get posts with high engagement (likes + comments) from the last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const { data, error } = await supabase
      .from('posts')
      .select(`
        *,
        profiles:user_id (
          id,
          username,
          full_name,
          avatar_url
        ),
        post_media (
          id,
          media_url,
          media_type,
          order_index,
          width,
          height
        ),
        post_hashtags (
          id,
          hashtag
        ),
        post_likes (
          id,
          user_id
        ),
        post_comments (
          id
        )
      `)
      .gte('created_at', sevenDaysAgo.toISOString())
      .order('created_at', { ascending: false })
      .limit(limit * 2); // Get more to filter by engagement

    if (error) {
      console.error('Supabase error in getPopularPosts:', error);
      return [];
    }

    const posts = transformPostsData(data || []);
    
    // Sort by engagement score (likes + comments * 2)
    return posts
      .map(post => ({
        ...post,
        engagementScore: (post.like_count || 0) + (post.comment_count || 0) * 2
      }))
      .sort((a, b) => b.engagementScore - a.engagementScore)
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting popular posts:', error);
    return [];
  }
};

export const getTrendingHashtags = async (limit: number = 10): Promise<{ hashtag: string; count: number }[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return [];
    }

    // Get hashtags from posts in the last 24 hours
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);

    const { data, error } = await supabase
      .from('post_hashtags')
      .select(`
        hashtag,
        posts!inner (
          created_at
        )
      `)
      .gte('posts.created_at', oneDayAgo.toISOString());

    if (error) {
      console.error('Supabase error in getTrendingHashtags:', error);
      return [];
    }

    // Count hashtag occurrences
    const hashtagCounts: { [key: string]: number } = {};
    (data || []).forEach(item => {
      hashtagCounts[item.hashtag] = (hashtagCounts[item.hashtag] || 0) + 1;
    });

    return Object.entries(hashtagCounts)
      .map(([hashtag, count]) => ({ hashtag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting trending hashtags:', error);
    return [];
  }
};

export const getRecommendedUsers = async (userId: string, limit: number = 10): Promise<User[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return [];
    }

    // Get users that the current user's follows also follow (mutual connections)
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .neq('id', userId)
      .limit(limit * 2);

    if (error) {
      console.error('Supabase error in getRecommendedUsers:', error);
      return [];
    }

    // For now, return random users (in a real app, this would be more sophisticated)
    const users = (data || []).map(profile => ({
      id: profile.id,
      username: profile.username,
      displayName: profile.full_name || profile.username,
      profileImage: profile.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
      bio: profile.bio || '',
      followerCount: 0,
      followingCount: 0,
      postCount: 0,
    }));

    // Shuffle and return limited results
    return users.sort(() => 0.5 - Math.random()).slice(0, limit);
  } catch (error) {
    console.error('Error getting recommended users:', error);
    return [];
  }
};

export const getAlgorithmicFeed = async (userId: string, limit: number = 20): Promise<Post[]> => {
  try {
    if (!supabase) {
      console.warn('Supabase client not initialized');
      return [];
    }

    // First, get the list of users that the current user follows
    const { data: followsData, error: followsError } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', userId);

    if (followsError) {
      console.error('Supabase error getting follows:', followsError);
      // Continue with empty follows list
    }

    const followingIds = (followsData || []).map(follow => follow.following_id);

    let followedPosts: Post[] = [];

    // If the user follows someone, get posts from followed users
    if (followingIds.length > 0) {
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select(`
          *,
          profiles:user_id (
            id,
            username,
            full_name,
            avatar_url
          ),
          post_media (
            id,
            media_url,
            media_type,
            order_index,
            width,
            height
          ),
          post_hashtags (
            id,
            hashtag
          ),
          post_likes (
            id,
            user_id
          ),
          post_comments (
            id
          )
        `)
        .in('user_id', followingIds)
        .order('created_at', { ascending: false })
        .limit(Math.floor(limit * 0.7)); // 70% from followed users

      if (postsError) {
        console.error('Supabase error getting followed posts:', postsError);
      } else {
        followedPosts = transformPostsData(postsData || []);
      }
    }

    // Get popular posts for discovery (30% of feed)
    const popularPosts = await getPopularPosts(Math.ceil(limit * 0.3));

    // Combine and deduplicate
    const allPosts = [...followedPosts, ...popularPosts];
    const uniquePosts = allPosts.filter((post, index, self) => 
      index === self.findIndex(p => p.id === post.id)
    );

    // Sort by a combination of recency and engagement
    return uniquePosts
      .map(post => {
        const recencyScore = Math.max(0, 100 - Math.floor((Date.now() - new Date(post.created_at).getTime()) / (1000 * 60 * 60))); // Hours ago
        const engagementScore = (post.like_count || 0) + (post.comment_count || 0) * 2;
        return {
          ...post,
          algorithmScore: recencyScore * 0.6 + engagementScore * 0.4
        };
      })
      .sort((a, b) => b.algorithmScore - a.algorithmScore)
      .slice(0, limit);
  } catch (error) {
    console.error('Error getting algorithmic feed:', error);
    return [];
  }
};

// Helper function to transform posts data
const transformPostsData = (data: any[]): Post[] => {
  return data.map(post => {
    const profile = post.profiles || {
      id: post.user_id,
      username: 'unknown',
      full_name: 'Unknown User',
      avatar_url: 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300'
    };

    return {
      id: post.id,
      user_id: post.user_id,
      user: {
        id: profile.id,
        username: profile.username,
        displayName: profile.full_name || profile.username,
        profileImage: profile.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: '',
        followerCount: 0,
        followingCount: 0,
        postCount: 0,
      },
      caption: post.caption || '',
      location: post.location,
      created_at: post.created_at,
      updated_at: post.updated_at,
      media: (post.post_media || [])
        .filter((media: any) => !media.media_url.includes('example.com'))
        .sort((a: any, b: any) => a.order_index - b.order_index),
      hashtags: post.post_hashtags || [],
      user_tags: [],
      likes: post.post_likes || [],
      comments: [],
      like_count: (post.post_likes || []).length,
      comment_count: (post.post_comments || []).length,
      is_liked: false,
      is_saved: false,
    };
  });
};